/**
 * L'avviso da mostrare quando una lettura da Odoo si avvicina al proprio
 * limite — o l'ha raggiunto.
 *
 * ## Perché sta qui e non nelle tre pagine
 *
 * Le sincronizzazioni sono tre (Clienti, Calendario, Interventi) e leggono
 * quattro entità. Se ognuna scrivesse il proprio messaggio, la stessa
 * situazione verrebbe raccontata in tre modi diversi — e il giorno che la
 * soglia cambia, andrebbe cambiata in tre posti. La regola su QUANDO avvisare
 * sta comunque nel backend (`utils/odooLettura.ts`): qui c'è solo come si dice.
 */

export interface EsitoLettura {
  letti: number;
  limite: number;
  /** La risposta è arrivata piena: non si può sapere se ce n'erano altri. */
  troncato: boolean;
  vicino_al_limite: boolean;
  margine: number;
}

export type LettureOdoo = Record<string, EsitoLettura>;

/** Nomi leggibili: in risposta arrivano gli identificativi dei modelli Odoo. */
const NOMI: Record<string, string> = {
  "res.partner": "contatti",
  "calendar.event": "eventi del calendario",
  "project.task": "assistenza sul campo",
  "helpdesk.ticket": "ticket Helpdesk",
};

/**
 * `null` quando non c'è niente da dire: chi chiama non deve costruire nessuna
 * frase e lascia il messaggio com'era.
 *
 * Le due situazioni restano distinte perché richiedono azioni diverse. Se la
 * lettura è PIENA qualcosa probabilmente manca già in app, ed è urgente; se è
 * solo vicina al limite non manca ancora niente, e c'è tempo per alzarlo con
 * calma. Dirle allo stesso modo renderebbe la prima meno grave di quanto sia.
 */
export function avvisoLettura(letture?: LettureOdoo): { testo: string; grave: boolean } | null {
  if (!letture) return null;

  const voci = Object.entries(letture);
  const piene = voci.filter(([, e]) => e.troncato);
  const vicine = voci.filter(([, e]) => e.vicino_al_limite && !e.troncato);

  if (piene.length > 0) {
    const elenco = piene
      .map(([m, e]) => `${NOMI[m] ?? m} (${e.letti} su ${e.limite})`)
      .join(", ");
    return {
      testo: `Lettura al limite su ${elenco}: potrebbero esserci record non sincronizzati. Va alzato il limite.`,
      grave: true,
    };
  }

  if (vicine.length > 0) {
    const elenco = vicine
      .map(([m, e]) => `${NOMI[m] ?? m} (${e.letti} su ${e.limite}, ${e.margine} di margine)`)
      .join(", ");
    return { testo: `Lettura vicina al limite su ${elenco}.`, grave: false };
  }

  return null;
}
